import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import './card.less';

export function Card({
    children,
    className,
    elevation = 1,
    padding = 'md',
    isInteractive = false,
    onClick = undefined,
    as: Component = 'div',
    ...rest
}) {
    const isDivButton = (isInteractive || Boolean(onClick)) && Component === 'div';
    const rootClassName = classNames(
        'vs-card',
        `has-elevation-${elevation}`,
        `has-padding-${padding}`,
        {
            'is-interactive': isInteractive || Boolean(onClick)
        },
        className
    );

    function handleKeyDown(event) {
        if (rest.onKeyDown) {
            rest.onKeyDown(event);
        }
        if (event.defaultPrevented) {
            return;
        }
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            if (onClick) {
                onClick(event);
            }
        }
    }

    return (
        <Component
            {...rest}
            className={rootClassName}
            onClick={onClick}
            role={isDivButton ? 'button' : rest.role}
            tabIndex={isDivButton ? 0 : rest.tabIndex}
            onKeyDown={isDivButton ? handleKeyDown : rest.onKeyDown}
        >
            {children}
        </Component>
    );
}

export function CardHeader({
    title, description, action, className
}) {
    if (!title && !description && !action) {
        return null;
    }

    return (
        <header className={classNames('vs-card__header', className)}>
            <div className="vs-card__header-text">
                {title && <h3 className="vs-card__title">{title}</h3>}
                {description && (
                    <p className="vs-card__description">{description}</p>
                )}
            </div>
            {action && <div className="vs-card__header-action">{action}</div>}
        </header>
    );
}

export function CardBody({children, className}) {
    return (
        <div className={classNames('vs-card__body', className)}>
            {children}
        </div>
    );
}

export function CardFooter({children, className}) {
    return (
        <footer className={classNames('vs-card__footer', className)}>
            {children}
        </footer>
    );
}

Card.Header = CardHeader;
Card.Body = CardBody;
Card.Footer = CardFooter;

Card.propTypes = {
    children: PropTypes.node,
    className: PropTypes.string,
    elevation: PropTypes.oneOf([0, 1, 2, 3]),
    padding: PropTypes.oneOf(['none', 'sm', 'md', 'lg']),
    isInteractive: PropTypes.bool,
    onClick: PropTypes.func,
    as: PropTypes.elementType
};

CardHeader.propTypes = {
    title: PropTypes.node,
    description: PropTypes.node,
    action: PropTypes.node,
    className: PropTypes.string
};

CardBody.propTypes = {
    children: PropTypes.node,
    className: PropTypes.string
};

CardFooter.propTypes = {
    children: PropTypes.node,
    className: PropTypes.string
};
